'use client'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'

/**
 * App screenshots as a quiet strip of thumbnails. Tapping one zooms the whole
 * strip up and scrolls that shot into view — at the zoomed size roughly one
 * and a half shots fit the column, so the cut-off edge says "scroll sideways"
 * without a hint arrow. Tapping again folds it back to the strip.
 *
 * The zoom grows the row several hundred pixels, which on a phone pushes most
 * of it past the fold, so the page follows it up. Centred rather than pinned to
 * the top: a zoomed shot is shorter than the screen, so centring costs nothing
 * and keeps some page either side of it.
 *
 * Ported from the Balansebarn product pages. Two things differ, both forced by
 * this site's chrome: the gutter matches the 36px section padding rather than
 * Balansebarn's 18px article column, and the centring makes room for the navbar
 * (see STICKY_CHROME) instead of asking it to stay hidden — over there the
 * header hides itself on a scroll it didn't start, here it is always on.
 */

/**
 * The strip starts flush with its container's left edge — in the hero that is
 * the headline's edge, which is what a shot pinned by the zoom lines up with.
 * Kept as a named constant because the pin maths reads better for it.
 */
const GUTTER = 0

/** navbar (64px) + the hairline above it, which stays pinned over the page */
const STICKY_CHROME = 65

/** The App Store shots, banner cropped off. Alt text lives in messages. */
const SHOTS = [
  { src: '/images/app-screenshots/screen-1.webp', key: 'shotOverview' },
  { src: '/images/app-screenshots/screen-2.webp', key: 'shotCalendars' },
  { src: '/images/app-screenshots/screen-3.webp', key: 'shotCustomize' },
  { src: '/images/app-screenshots/screen-4.webp', key: 'shotAdding' },
  { src: '/images/app-screenshots/screen-5.webp', key: 'shotTasks' },
  { src: '/images/app-screenshots/screen-6.webp', key: 'shotAgenda' },
  { src: '/images/app-screenshots/screen-7.webp', key: 'shotRoutines' },
  { src: '/images/app-screenshots/screen-8.webp', key: 'shotWeather' },
  { src: '/images/app-screenshots/screen-9.webp', key: 'shotMeals' },
  { src: '/images/app-screenshots/screen-10.webp', key: 'shotSummary' },
] as const

/**
 * Broadcast the zoom state. The social-proof bar is pinned to the bottom of the
 * window and would sit on top of an opened shot, but it lives outside this
 * component's tree, so a DOM event carries the news rather than threading state
 * up through the hero and back down the page.
 */
export const SCREENSHOT_ZOOM_EVENT = 'homie:screenshots-zoom'

export function AppScreenshots() {
  const t = useTranslations('appScreenshots')
  const [zoomed, setZoomed] = useState(false)
  const scroller = useRef<HTMLDivElement>(null)
  const items = useRef<(HTMLButtonElement | null)[]>([])

  useEffect(() => {
    window.dispatchEvent(new CustomEvent(SCREENSHOT_ZOOM_EVENT, { detail: { zoomed } }))
  }, [zoomed])

  function handleClick(i: number) {
    if (zoomed) {
      setZoomed(false)
      return
    }
    setZoomed(true)
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    // Wait out the growth before moving the page. Mid-transition the row has no
    // final height yet, so centring on what it measures now would land short.
    // Reduced motion skips the transition, so there is nothing to wait for.
    window.setTimeout(
      () => {
        const box = scroller.current
        if (!box) return
        const rect = box.getBoundingClientRect()
        // Centre within the band the navbar leaves visible, not the whole
        // viewport — otherwise the top of the zoomed strip sits under it.
        const room = window.innerHeight - STICKY_CHROME - rect.height
        // Taller than that band (a very short window) can't be centred without
        // cutting the top off, so sit it just under the navbar instead.
        const offset = STICKY_CHROME + (room > 0 ? room / 2 : GUTTER)
        window.scrollTo({
          top: Math.max(0, rect.top + window.scrollY - offset),
          behavior: reduce ? 'auto' : 'smooth',
        })
      },
      reduce ? 0 : 220
    )
    // Keep the tapped shot pinned to the left edge while the row grows: the
    // shots are still mid-transition, so re-pin every frame until it settles
    // instead of measuring once. This one scrolls the strip, not the page.
    let frames = 24 // ~400ms, comfortably past the 200ms growth
    const pin = () => {
      const box = scroller.current
      const el = items.current[i]
      if (!box || !el) return
      // GUTTER keeps the pinned shot aligned with the text column, since the
      // strip itself bleeds out to the section edges.
      box.scrollLeft +=
        el.getBoundingClientRect().left - box.getBoundingClientRect().left - GUTTER
      if (--frames > 0) requestAnimationFrame(pin)
    }
    requestAnimationFrame(pin)
  }

  function handleHintClick() {
    if (zoomed) {
      setZoomed(false)
      return
    }
    const box = scroller.current
    let nearest = 0
    if (box) {
      const left = box.getBoundingClientRect().left
      let best = Infinity
      items.current.forEach((el, i) => {
        if (!el) return
        const d = Math.abs(el.getBoundingClientRect().left - left)
        if (d < best) {
          best = d
          nearest = i
        }
      })
    }
    handleClick(nearest)
  }

  return (
    <>
    {/* A handwritten aside, the one sanctioned break from Lato (the cookie
        banner uses the same accent). Above the strip rather than below it, so
        it is read before the thing it describes. aria-hidden because each shot
        already carries this instruction in its own label, and a screen reader
        does not need it a second time in a decorative voice. */}
    <button
      type="button"
      aria-hidden="true"
      tabIndex={-1}
      onClick={handleHintClick}
      className="mb-[10px] block w-fit cursor-pointer origin-left -rotate-2 select-none text-[26px] leading-none text-[#5F875E]"
      style={{ fontFamily: 'var(--font-caveat), cursive' }}
    >
      {zoomed ? t('hintZoomOut') : t('hintZoomIn')}
    </button>
    <div
      ref={scroller}
      className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {/* The trailing gap lives on the row, not the scroller: padding-right on a
          scroll container is dropped when you reach the end in some browsers,
          which would leave the last shot flush against the screen edge. */}
      <div
        className={`flex w-max gap-2 pr-[24px] ${zoomed ? 'snap-x snap-mandatory' : ''}`}
      >
        {SHOTS.map((shot, i) => (
          <button
            key={shot.src}
            type="button"
            ref={(el) => {
              items.current[i] = el
            }}
            onClick={() => handleClick(i)}
            aria-expanded={zoomed}
            aria-label={`${t('shotLabel', {
              alt: t(shot.key),
              index: i + 1,
              total: SHOTS.length,
            })} ${zoomed ? t('zoomOut') : t('zoomIn')}`}
            className={`aspect-[460/913] flex-none cursor-pointer snap-start overflow-hidden border border-stroke-light bg-surface-primary transition-[height] duration-200 ease-out motion-reduce:transition-none ${
              zoomed
                ? // 595px tall is 300px wide at this aspect — the size where the
                  // screens are actually readable. Same on a phone, where 300px
                  // still clears the gutter.
                  'h-[595px] rounded-3xl'
                : // ~125px wide at the smallest size, matching the Balansebarn
                  // strip — small enough to read as a teaser, big enough that
                  // the screens are recognisable before you tap one.
                  'h-[248px] rounded-xl sm:h-[280px] lg:h-[320px] lg:rounded-2xl'
            }`}
          >
            <Image
              src={shot.src}
              alt=""
              width={460}
              height={913}
              sizes="(min-width: 640px) 310px, 240px"
              /* The strip is the hero visual now, so its first shot is the
                 Largest Contentful Paint. Lazy-loading the LCP element delays
                 it; the rest of the strip stays lazy. */
              loading={i === 0 ? 'eager' : 'lazy'}
              fetchPriority={i === 0 ? 'high' : undefined}
              className="h-full w-full object-cover"
            />
          </button>
        ))}
      </div>
    </div>

    </>
  )
}
